import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { orderBy as _orderBy } from 'lodash-es';

import { API_STATUS } from 'store/constants';
import { fetchChannelPlaylistsWithVideos } from './libraryAPI';
import { getLocalStorageValue, mapAllPlaylistFilter, mapAllPlaylistVideos, filterPlaylistVideos } from './libSliceUtil';

const initialState = {
	loaded: false,
	searchText: '',
	filterType: 'all',
	playlists: [],
	videos: [],
	playlistFilters: []
};

const cachedLibrary = getLocalStorageValue('sim_lib');
const cachedVideos = getLocalStorageValue('sim_lib_videos');
const playlistFilters = getLocalStorageValue('sim_lib_filters');
//Manage playlists library cache
if (cachedLibrary) {
	initialState.loaded = true;
	initialState.playlists = [...cachedLibrary];
}
//Manage videos cache
if (cachedVideos) initialState.videos = { ...cachedVideos };
//Manage playlist filters cache
if (playlistFilters) initialState.playlistFilters = [...playlistFilters];

// thunk & service
export const getChannelPlaylistsWithVideoDetailsAsync = createAsyncThunk(
	'library/fetchChannelPlaylistsWithVideos',
	async () => {
		const channelPlaylistsWithVideoDetails = await fetchChannelPlaylistsWithVideos();
		return channelPlaylistsWithVideoDetails?.length ? channelPlaylistsWithVideoDetails : [];
	}
);

export const librarySlice = createSlice({
	name: 'library',
	initialState,
	reducers: {},
	extraReducers: builder => {
		builder
			.addCase(getChannelPlaylistsWithVideoDetailsAsync.pending, state => {
				state.status = API_STATUS.PENDING;
			})
			.addCase(getChannelPlaylistsWithVideoDetailsAsync.rejected, state => {
				state.status = API_STATUS.REJECTED;
			})
			.addCase(getChannelPlaylistsWithVideoDetailsAsync.fulfilled, (state, action) => {
				//Cache the playlists with video details
				const orderedPlaylists = _orderBy(action.payload, ['title'], ['asc']);
				localStorage.setItem('sim_lib', JSON.stringify(orderedPlaylists));
				state.playlists = [...orderedPlaylists];

				//Cache the videos
				const playlistVideos = mapAllPlaylistVideos(orderedPlaylists);
				state.videos = playlistVideos;
				localStorage.setItem('sim_lib_videos', JSON.stringify(playlistVideos));

				//Playlists filter
				const playlistFilters = mapAllPlaylistFilter(orderedPlaylists);
				localStorage.setItem('sim_lib_filters', JSON.stringify(playlistFilters));
				state.playlistFilters = playlistFilters;

				//Mark as loaded
				state.loaded = true;
				state.status = API_STATUS.COMPLETED;
			});
	}
});

export const selectLibraryVideoById = videoId => state => state.library.videos[videoId];
export const selectPlaylistsWithVideos = (playlistId, searchText) => state => {
	if (!searchText)
		return playlistId === 'all'
			? state.library.playlists
			: state.library.playlists.filter(pl => pl.id === playlistId);

	if (playlistId !== 'all') {
		const curPlaylist = state.library.playlists.find(({ id }) => id === playlistId);

		return [filterPlaylistVideos(curPlaylist, searchText)];
	}

	return state.library.playlists
		.map(playlist => filterPlaylistVideos(playlist, searchText))
		.filter(playlist => playlist.videos.length);
};
export const selectPlaylistsWithTitle = state => state.library.playlistFilters;
export const selectPlaylistFilterById = playlistId => state => {
	return state.library.playlistFilters.find(({ id }) => id === playlistId);
};
export const selectHasLibraryItemsLoaded = state => state.library.loaded;

export default librarySlice.reducer;
